import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';

import { ProductNotFoundError } from '@app-types/error';
import type { UserType } from '@app-types/user';

import { HttpStatus } from '@constants/http';

const appConfig = getAppConfig();

export class SapiRepository {
    url: string;

    public constructor(url: string) {
        this.url = url;
    }

    private buildQueryParams(params: Record<string, string | boolean | string[]>): string {
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                searchParams.set(key, value.join(','));
            } else if (typeof value === 'boolean') {
                searchParams.set(key, value ? 'true' : 'false');
            } else {
                searchParams.set(key, value);
            }
        });

        return searchParams.toString();
    }

    public getProductByGencode<T>(gencode: string, withParent: boolean = false): Promise<T> {
        const query = this.buildQueryParams({ parent: withParent });
        return this.getProducts(`/${gencode}?${query}`);
    }

    public async getProductByUrl<T>(url: string, withParent: boolean = false): Promise<T> {
        const query = this.buildQueryParams({
            attribute: 'url_key',
            parent: withParent,
        });
        return this.getProducts(`/${url}?${query}`);
    }

    public async getProductsByGencodes<T>(gencodes: string[], user?: UserType): Promise<T> {
        if (gencodes.length === 0) {
            throw new Error('No gencodes provided');
        }

        const query = this.buildQueryParams({ listGencode: gencodes });
        return this.getProducts(`?${query}`, user);
    }

    public async getProducts<T>(url: string, user?: UserType): Promise<T> {
        return apiHttpService
            .get<T>(`${this.url}/ventes/produits${url}`, undefined, {
                ttl: Number.parseInt(appConfig.cache.ttl.productDetail),
                tags: [CACHE_TAGS.PRODUCT_DETAIL],
                user,
            })
            .catch((error) => {
                if (error?.response?.status === HttpStatus.NOT_FOUND) {
                    throw new ProductNotFoundError(`Product not found ${url}`);
                } else {
                    throw error;
                }
            });
    }
}

export const sapiRepository = new SapiRepository(appConfig.b2r.url);
